/**
 * Lesezugriff auf die geöffnete Präsentation.
 *
 * Wie beim Word-Kurs arbeiten alle Prüfungen auf einer Momentaufnahme statt
 * direkt auf Office.js. Bei PowerPoint kommt eine Besonderheit dazu: Die
 * einzelnen Angaben stammen aus sehr unterschiedlichen Anforderungssätzen.
 * Folien gibt es ab 1.2, Formen ab 1.3, Text und Formtyp ab 1.4, die
 * Platzhalterart erst ab 1.8 und Alternativtexte ab 1.10.
 *
 * Deshalb wird die Momentaufnahme in Stufen gelesen: Was die laufende
 * PowerPoint-Version nicht kann, bleibt `null` und wird in `features`
 * ausdrücklich als nicht gelesen gemeldet. Eine Prüfung, die eine fehlende
 * Angabe braucht, meldet das über `unsupported()` offen – geraten wird nicht.
 */

import { powerPointApi, supportsPowerPointApi } from "./capabilities";

/** Schriftformatierung eines Textrahmens. `null` bei gemischter Formatierung. */
export interface ShapeFontState {
  name: string | null;
  size: number | null;
  bold: boolean | null;
  italic: boolean | null;
  /** PowerPoint-Werte wie "None", "Single", "Double". */
  underline: string | null;
  color: string | null;
}

export interface PlaceholderInfo {
  /** "Title", "Body", "CenterTitle", "Subtitle", "Picture", … */
  type: string;
  /** Was tatsächlich im Platzhalter steckt, z. B. "Image" oder "Table". */
  containedType: string;
}

export interface ShapeInfo {
  id: string;
  /** Nullbasierte Nummer der Folie, auf der die Form liegt. */
  slideIndex: number;
  /** Angezeigter Name im Auswahlbereich, z. B. "Titel 1". `null` vor 1.4. */
  name: string | null;
  /** "Placeholder", "TextBox", "Image", "Table", "Chart", … `null` vor 1.4. */
  type: string | null;
  /** Position und Größe in Punkt. `null` vor 1.4. */
  left: number | null;
  top: number | null;
  width: number | null;
  height: number | null;
  /** Platzhalterart. `null`, wenn die Form keiner ist oder 1.8 fehlt. */
  placeholder: PlaceholderInfo | null;
  /** Gesamter Text der Form. `null`, wenn er nicht gelesen werden konnte. */
  text: string | null;
  /** Die einzelnen Absätze des Textrahmens ohne Leerzeilen. */
  paragraphs: string[];
  font: ShapeFontState | null;
  /**
   * Sind Aufzählungszeichen sichtbar? `null` bei gemischtem Text – also
   * genau dann, wenn ein Teil der Absätze Punkte hat und ein anderer nicht.
   */
  bulletsVisible: boolean | null;
  /** "Left", "Center", "Right", "Justify" – `null` bei gemischter Ausrichtung. */
  horizontalAlignment: string | null;
  /** Alternativtext für Screenreader. `null`, wenn nicht lesbar (vor 1.10). */
  altText: string | null;
  /** Als rein dekorativ gekennzeichnet? `null`, wenn nicht lesbar (vor 1.10). */
  isDecorative: boolean | null;
}

export interface SlideInfo {
  /** Nullbasierte Position in der Präsentation. */
  index: number;
  id: string;
  /** Name des zugewiesenen Layouts, z. B. "Titel und Inhalt". */
  layoutName: string | null;
  layoutId: string | null;
  /** Art des Layouts, z. B. "Title", "SectionHeader". `null` vor 1.8. */
  layoutType: string | null;
  /** Name des Folienmasters, zu dem das Layout gehört. */
  masterName: string | null;
  shapes: ShapeInfo[];
}

/** Welche Angaben konnte diese PowerPoint-Version liefern? */
export interface PresentationFeatures {
  /** Formen einer Folie überhaupt lesbar (ab 1.3). */
  shapes: boolean;
  /** Name, Typ, Position und Größe der Formen (ab 1.4). */
  shapeDetails: boolean;
  /** Text der Formen (ab 1.4, kann trotzdem einzeln fehlschlagen). */
  text: boolean;
  /** Platzhalterart der Formen (ab 1.8). */
  placeholders: boolean;
  /** Art des Folienlayouts (ab 1.8). */
  layoutType: boolean;
  /** Alternativtexte (ab 1.10). */
  altText: boolean;
}

export interface PresentationSnapshot {
  slides: SlideInfo[];
  features: PresentationFeatures;
}

/** Fehler, der geworfen wird, wenn das Add-in nicht in PowerPoint läuft. */
export class PowerPointUnavailableError extends Error {
  constructor() {
    super(
      "Es ist gerade keine PowerPoint-Präsentation verbunden. Bitte öffne den " +
        "Kurs über das Add-in-Panel in PowerPoint.",
    );
    this.name = "PowerPointUnavailableError";
  }
}

export function isPowerPointAvailable(): boolean {
  return typeof PowerPoint !== "undefined" && typeof PowerPoint.run === "function";
}

/**
 * Liest eine geladene Eigenschaft ab, ohne die gesamte Prüfung zu sprengen.
 *
 * Office.js wirft beim Zugriff auf eine nicht geladene Eigenschaft einen
 * Fehler. Weil je nach PowerPoint-Version unterschiedlich viel geladen werden
 * konnte, wird jeder Zugriff einzeln abgesichert.
 */
function value<T>(read: () => T): T | null {
  try {
    const result = read();
    return result === undefined ? null : result;
  } catch {
    return null;
  }
}

function text(read: () => unknown): string | null {
  const result = value(read);
  return typeof result === "string" ? result : null;
}

function number(read: () => unknown): number | null {
  const result = value(read);
  return typeof result === "number" && Number.isFinite(result) ? result : null;
}

function flag(read: () => unknown): boolean | null {
  const result = value(read);
  return typeof result === "boolean" ? result : null;
}

/**
 * Formarten, die einen Textrahmen haben können. Bei einem Bild oder einer
 * Tabelle den Textrahmen anzufordern, lässt den ganzen Abruf scheitern –
 * deshalb wird vorher gefiltert.
 */
const TEXT_SHAPE_TYPES = new Set(["Placeholder", "TextBox", "GeometricShape", "Callout", "Freeform"]);

/** Absätze aus dem Text eines Textrahmens. PowerPoint trennt sie mit \r oder \n. */
export function splitParagraphs(value: string): string[] {
  return value
    .split(/[\r\n\v]+/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);
}

/** Liest die gesamte Präsentation aus und bereitet sie für die Prüfungen auf. */
export async function readPresentation(): Promise<PresentationSnapshot> {
  if (!isPowerPointAvailable()) throw new PowerPointUnavailableError();

  const canReadShapes = supportsPowerPointApi(powerPointApi.shapes);
  const canReadDetails = supportsPowerPointApi(powerPointApi.shapeDetails);
  const canReadPlaceholders = supportsPowerPointApi(powerPointApi.placeholders);
  const canReadLayoutType = supportsPowerPointApi(powerPointApi.layoutType);
  const canReadAltText = supportsPowerPointApi(powerPointApi.altText);

  const features: PresentationFeatures = {
    shapes: canReadShapes,
    shapeDetails: canReadShapes && canReadDetails,
    text: false,
    placeholders: canReadShapes && canReadDetails && canReadPlaceholders,
    layoutType: canReadShapes && canReadLayoutType,
    altText: canReadShapes && canReadDetails && canReadAltText,
  };

  return PowerPoint.run(async (context) => {
    const slideCollection = context.presentation.slides;
    slideCollection.load("items/id");
    await context.sync();

    const slideItems = slideCollection.items;

    if (!canReadShapes) {
      return {
        features,
        slides: slideItems.map((slide, index) => ({
          index,
          id: text(() => slide.id) ?? String(index),
          layoutName: null,
          layoutId: null,
          layoutType: null,
          masterName: null,
          shapes: [],
        })),
      };
    }

    // Stufe 2: Layout, Folienmaster und die Formen jeder Folie.
    const shapeProperties = ["items/id"];
    if (canReadDetails) {
      shapeProperties.push(
        "items/name",
        "items/type",
        "items/left",
        "items/top",
        "items/width",
        "items/height",
      );
    }
    if (canReadAltText) {
      shapeProperties.push("items/altTextDescription", "items/isDecorative");
    }

    const layoutProperties = canReadLayoutType ? "name,id,type" : "name,id";

    const perSlide = slideItems.map((slide) => {
      slide.layout.load(layoutProperties);
      slide.slideMaster.load("name");
      slide.shapes.load(shapeProperties.join(","));
      return slide;
    });
    await context.sync();

    // Stufe 3: Platzhalterart – nur für Formen, die tatsächlich Platzhalter
    // sind. Für alle anderen liefert PowerPoint keinen Platzhalterdatensatz.
    const placeholderRequests = new Map<string, PowerPoint.PlaceholderFormat>();
    if (features.placeholders) {
      for (const slide of perSlide) {
        for (const shape of slide.shapes.items) {
          if (value(() => shape.type) !== "Placeholder") continue;
          const format = shape.placeholderFormat;
          format.load("type,containedType");
          placeholderRequests.set(value(() => shape.id) ?? "", format);
        }
      }
      try {
        await context.sync();
      } catch {
        features.placeholders = false;
        placeholderRequests.clear();
      }
    }

    // Stufe 4: Text der Formen. Bewusst als eigener Abruf, damit ein einzelner
    // Formtyp ohne Textrahmen nicht die übrigen Angaben mitreißt.
    const textRequests = new Map<string, PowerPoint.TextFrame>();
    if (canReadDetails) {
      for (const slide of perSlide) {
        for (const shape of slide.shapes.items) {
          const type = value(() => shape.type);
          if (typeof type === "string" && !TEXT_SHAPE_TYPES.has(type)) continue;
          const frame = shape.textFrame;
          frame.load("hasText");
          frame.textRange.load("text");
          frame.textRange.font.load("name,size,bold,italic,underline,color");
          frame.textRange.paragraphFormat.load("horizontalAlignment");
          frame.textRange.paragraphFormat.bulletFormat.load("visible");
          textRequests.set(value(() => shape.id) ?? "", frame);
        }
      }
      try {
        await context.sync();
        features.text = true;
      } catch {
        features.text = false;
        textRequests.clear();
      }
    }

    const slides: SlideInfo[] = perSlide.map((slide, index) => ({
      index,
      id: text(() => slide.id) ?? String(index),
      layoutName: text(() => slide.layout.name),
      layoutId: text(() => slide.layout.id),
      layoutType: text(() => slide.layout.type),
      masterName: text(() => slide.slideMaster.name),
      shapes: slide.shapes.items.map((shape) => {
        const id = text(() => shape.id) ?? "";
        const frame = textRequests.get(id);
        const shapeText = frame ? text(() => frame.textRange.text) : null;
        const placeholderFormat = placeholderRequests.get(id);

        return {
          id,
          slideIndex: index,
          name: text(() => shape.name),
          type: text(() => shape.type),
          left: number(() => shape.left),
          top: number(() => shape.top),
          width: number(() => shape.width),
          height: number(() => shape.height),
          placeholder: placeholderFormat
            ? {
                type: text(() => placeholderFormat.type) ?? "Unsupported",
                containedType: text(() => placeholderFormat.containedType) ?? "Unsupported",
              }
            : null,
          text: shapeText,
          paragraphs: shapeText ? splitParagraphs(shapeText) : [],
          font: frame
            ? {
                name: text(() => frame.textRange.font.name),
                size: number(() => frame.textRange.font.size),
                bold: flag(() => frame.textRange.font.bold),
                italic: flag(() => frame.textRange.font.italic),
                underline: text(() => frame.textRange.font.underline),
                color: text(() => frame.textRange.font.color),
              }
            : null,
          bulletsVisible: frame
            ? flag(() => frame.textRange.paragraphFormat.bulletFormat.visible)
            : null,
          horizontalAlignment: frame
            ? text(() => frame.textRange.paragraphFormat.horizontalAlignment)
            : null,
          altText: text(() => shape.altTextDescription),
          isDecorative: flag(() => shape.isDecorative),
        };
      }),
    }));

    return { slides, features };
  });
}

/* ---------- Auswertung der Momentaufnahme ---------- */

/** Alle Formen der Präsentation, folienübergreifend. */
export function allShapes(snapshot: PresentationSnapshot): ShapeInfo[] {
  return snapshot.slides.flatMap((slide) => slide.shapes);
}

/** Ist die Form ein Platzhalter des Folienlayouts? */
export function isPlaceholder(shape: ShapeInfo): boolean {
  return shape.type === "Placeholder";
}

/**
 * Ist die Form ein frei gezogenes Textfeld?
 *
 * Genau diese Formen sind der rote Faden des Kurses: Sie sehen aus wie ein
 * Platzhalter, gehören aber zu keinem Layout – und fallen deshalb bei jeder
 * Designänderung und in der Gliederungsansicht durchs Raster.
 */
export function isFreeTextBox(shape: ShapeInfo): boolean {
  return shape.type === "TextBox";
}

/** Alle frei gezogenen Textfelder einer Folie. */
export function freeTextBoxes(slide: SlideInfo): ShapeInfo[] {
  return slide.shapes.filter(isFreeTextBox);
}

const TITLE_PLACEHOLDERS = new Set(["Title", "CenterTitle", "VerticalTitle"]);
const BODY_PLACEHOLDERS = new Set(["Body", "Content", "VerticalBody", "VerticalContent", "Subtitle"]);

/**
 * Titelplatzhalter einer Folie.
 *
 * Erster Weg: die Platzhalterart aus `placeholderFormat` (ab PowerPointApi
 * 1.8). Fehlt sie, wird ersatzweise über den Formnamen gesucht – PowerPoint
 * benennt Titelplatzhalter je nach Sprachversion "Title 1" oder "Titel 1".
 * Der Ersatzweg ist schwächer, aber ehrlicher als gar keine Prüfung.
 */
export function titlePlaceholder(slide: SlideInfo): ShapeInfo | null {
  const byType = slide.shapes.find(
    (shape) => shape.placeholder !== null && TITLE_PLACEHOLDERS.has(shape.placeholder.type),
  );
  if (byType) return byType;

  return (
    slide.shapes.find(
      (shape) => isPlaceholder(shape) && /^(title|titel)\b/i.test(shape.name ?? ""),
    ) ?? null
  );
}

/** Inhalts- und Textplatzhalter einer Folie, ohne den Titel. */
export function bodyPlaceholders(slide: SlideInfo): ShapeInfo[] {
  const byType = slide.shapes.filter(
    (shape) => shape.placeholder !== null && BODY_PLACEHOLDERS.has(shape.placeholder.type),
  );
  if (byType.length > 0) return byType;

  const title = titlePlaceholder(slide);
  return slide.shapes.filter((shape) => isPlaceholder(shape) && shape.id !== title?.id);
}

/** Alle Platzhalter einer Folie, die Text enthalten. */
export function filledPlaceholders(slide: SlideInfo): ShapeInfo[] {
  return slide.shapes.filter((shape) => isPlaceholder(shape) && shape.paragraphs.length > 0);
}

/** Trägt die Folie einen Titel mit sichtbarem Text? */
export function hasTitleText(slide: SlideInfo): boolean {
  const title = titlePlaceholder(slide);
  return title !== null && title.paragraphs.length > 0;
}

/** Wortzahl eines Textes – Grundlage für „Stichpunkt statt Fließtext“. */
export function countWords(value: string): number {
  return value.split(/\s+/).filter((word) => /\p{L}|\p{N}/u.test(word)).length;
}

/** Längster Absatz einer Form, gemessen in Wörtern. */
export function longestParagraphWords(shape: ShapeInfo): number {
  return shape.paragraphs.reduce((longest, paragraph) => {
    return Math.max(longest, countWords(paragraph));
  }, 0);
}

/** Gesamte Wortzahl aller Textplatzhalter einer Folie. */
export function slideWordCount(slide: SlideInfo): number {
  return slide.shapes.reduce((total, shape) => {
    return total + shape.paragraphs.reduce((sum, paragraph) => sum + countWords(paragraph), 0);
  }, 0);
}

/**
 * Endet der Absatz wie ein ganzer Satz?
 *
 * Ein Stichpunkt ist eine Kurzform, kein Satz. Punkte am Ende sind deshalb ein
 * verlässliches Zeichen dafür, dass jemand Fließtext auf die Folie geschrieben hat.
 */
export function looksLikeSentence(paragraph: string): boolean {
  return /[.!?]["“»)]?$/.test(paragraph.trim()) && countWords(paragraph) >= 5;
}

/** Alle Formen eines bestimmten Typs, folienübergreifend. */
export function shapesOfType(snapshot: PresentationSnapshot, type: string): ShapeInfo[] {
  return allShapes(snapshot).filter((shape) => shape.type === type);
}
